import React, { useMemo, useState } from 'react';

// "N devices can be assigned" banner for the Locations page.
//
// The same apply the Settings screen runs, surfaced where the sites and sub-locations
// actually are — mapping a project and then hunting through Settings to make devices
// appear under it is two screens for one job.
//
// Still a click, not automatic: assigning rewrites device.location and appends to each
// device's locationLog, so it stays a decision someone makes rather than something that
// happens on page load. `moves` is portalMoves from App.js — devices the portal wants to
// move that nothing is blocking.

function PortalAssignBanner({ moves, onAssign, isReadOnly }) {
  const [open, setOpen] = useState(false);

  // Grouped by destination site, then by project within it — the same two levels the
  // Locations page itself shows, so the preview reads as "what will appear where".
  const bySite = useMemo(() => {
    const sites = new Map();
    (moves || []).forEach(m => {
      const site = m.target || '—';
      if (!sites.has(site)) sites.set(site, { site, total: 0, projects: new Map() });
      const s = sites.get(site);
      s.total += 1;
      const p = m.projectName || 'No project';
      s.projects.set(p, (s.projects.get(p) || 0) + 1);
    });
    return [...sites.values()]
      .map(s => ({ ...s, projects: [...s.projects.entries()].sort((a, b) => b[1] - a[1]) }))
      .sort((a, b) => b.total - a.total);
  }, [moves]);

  if (!moves || !moves.length) return null;

  const siteCount = bySite.length;
  const btn = {
    display: 'inline-flex', alignItems: 'center', height: 30, padding: '0 12px', borderRadius: 6,
    border: '1px solid var(--border-default)', background: 'var(--surface-default)',
    color: 'var(--text-primary)', fontFamily: 'inherit', fontSize: 13, cursor: 'pointer',
  };

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16, borderColor: 'var(--info-border)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <span aria-hidden="true" style={{ flexShrink: 0, width: 8, height: 8, borderRadius: 9999, background: 'var(--info-text)' }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
            {moves.length} device{moves.length === 1 ? '' : 's'} can be assigned from the OWL portal
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            Going to {siteCount} location{siteCount === 1 ? '' : 's'} · devices in quarantine, RMA or signed out to someone are never moved
          </div>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)} style={btn}>
          {open ? 'Hide' : 'Review'}
        </button>
        {!isReadOnly && (
          <button type="button" onClick={onAssign} className="btn btn-primary" style={{ height: 30 }}>
            Assign {moves.length} device{moves.length === 1 ? '' : 's'}
          </button>
        )}
      </div>

      {open && (
        <div style={{ borderTop: '1px solid var(--border-default)' }}>
          {bySite.map(s => (
            <div key={s.site} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, padding: '0 16px', background: 'var(--surface-inset)' }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.site}</span>
                <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.total}</span>
              </div>
              {s.projects.map(([name, n]) => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32, padding: '0 16px 0 28px' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{n}</span>
                </div>
              ))}
            </div>
          ))}
          <div style={{ padding: '10px 16px', background: 'var(--surface-inset)', fontSize: 12, color: 'var(--text-muted)' }}>
            Each device keeps a record of the move in its history. Devices matched to the portal by
            ID rather than serial are never assigned automatically — they are listed in
            Settings &rarr; Portal Sync for checking.
          </div>
        </div>
      )}
    </div>
  );
}

export default PortalAssignBanner;
